import type { ReactNode } from "react"
import { format, isThisYear, isToday } from "date-fns"
import {
	IconTriangleWarningOutline18 as AlertTriangle,
	IconCheckOutline18 as Check,
	IconCheckDoubleOutline18 as CheckCheck,
	IconEnvelopeArrowRightOutline18 as EnvelopeArrowRight,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { AvatarProps } from "./avatar"
import { Avatar } from "./avatar"
import { Button } from "./button"
import { shouldShowIdentityTooltip } from "./identity-tooltip"
import { IdentityTooltipContent } from "./identity-tooltip-content"
import { resolveSenderIdentity } from "./sender-identity"
import {
	BARE_COMMENT_ROW_CLASS,
	MESSAGE_FOOTER_CLASS,
	messageSurfaceClasses,
	messageTimestampClasses,
} from "./slack-style-message-surface"
import { Tooltip } from "./tooltip"
import { ZoomableContent } from "./zoomable-content"

// Width of the avatar column, so a reply without an avatar still lines up under the parent body.
export const MESSAGE_AVATAR_SLOT_CLASS = "w-6"

export { MESSAGE_SURFACE_GUTTER_CLASS } from "./slack-style-message-surface"

type MessageSource = "email" | "slack" | "teams" | "live-chat" | "widget" | null

interface SlackStyleMessageProps {
	showAvatar?: boolean
	senderName?: string
	// Shown when neither the sender nor the company is known, so the name row keeps its timestamp.
	senderNameFallback?: string
	senderEmail?: string | null
	senderImage?: string | null
	timestamp: Date
	variant?: "standard" | "comment" | "notes"
	// Render a comment as a bare row (no yellow box), for grouping several
	// comments/replies inside a single shared box.
	bareComment?: boolean
	// Indent the comment body to align under the sender name (used for replies).
	indentContent?: boolean
	children: ReactNode
	footer?: ReactNode
	// Small label after the timestamp.
	badge?: ReactNode
	// Right-aligned controls in the sender row, so they line up with the avatar.
	actions?: ReactNode
	// Lets the actions slot give way before the sender name when both do not fit.
	shrinkActions?: boolean
	avatarContent?: ReactNode
	avatarSize?: AvatarProps["size"]
	timestampLink?: string
	onHideNotes?: () => void
	onClick?: () => void
	companyName?: string | null
	companyLogoUrl?: string | null
	senderIsUnverified?: boolean
	readAt?: Date | null
	// Set when the live chat reply went out as an email after the customer stayed silent.
	emailedAt?: Date | null
	emailedTo?: string | null
	source?: MessageSource
	isOutbound?: boolean
	showBackground?: boolean
}

export function emailedReceiptLabel({
	sentAt,
	emailedAt,
	recipient,
}: {
	sentAt: Date
	emailedAt: Date
	recipient?: string | null
}) {
	const minutes = Math.max(
		1,
		Math.round((emailedAt.getTime() - sentAt.getTime()) / 60_000),
	)
	const wait = minutes === 1 ? "a minute" : `${minutes} minutes`
	const destination = recipient ? `to ${recipient}` : "by email"
	return `No reply in chat for ${wait}, so a copy went ${destination} at ${format(emailedAt, "HH:mm")}`
}

export function SlackStyleMessage({
	showAvatar = true,
	senderName,
	senderNameFallback,
	senderEmail,
	senderImage,
	timestamp,
	variant = "standard",
	bareComment = false,
	indentContent = false,
	children,
	footer,
	badge,
	actions,
	shrinkActions = false,
	avatarContent,
	avatarSize = "s",
	timestampLink,
	onHideNotes,
	onClick,
	companyName,
	companyLogoUrl,
	senderIsUnverified,
	readAt,
	emailedAt,
	emailedTo,
	source,
	isOutbound = false,
	showBackground = true,
}: SlackStyleMessageProps) {
	const isComment = variant === "comment"
	const isNotes = variant === "notes"
	const { displayName, hasKnownSender } = resolveSenderIdentity({
		senderName,
		companyName,
		fallbackName: senderNameFallback,
	})
	const isUnverified = senderIsUnverified ?? false

	const isGravatarImageBroken = !!senderImage?.endsWith("?d=404") // currently we have a ton of bugged gravatar images, as they were not properly handled

	const avatarTooltipLabel: ReactNode = senderEmail ? (
		<IdentityTooltipContent name={displayName} email={senderEmail} />
	) : (
		displayName
	)

	// The sender name already renders next to the avatar, so the tooltip only earns its place when it carries the email.
	const showIdentityTooltip = shouldShowIdentityTooltip({
		visibleText: displayName,
		detail: senderEmail,
	})

	const resolvedImage = isGravatarImageBroken
		? companyLogoUrl
		: senderName
			? senderImage
			: (senderImage ?? companyLogoUrl)

	const renderAvatarContent = () => {
		if (avatarContent) return avatarContent
		return (
			<Tooltip
				label={avatarTooltipLabel}
				shouldWrapChildren
				enabled={showIdentityTooltip}
			>
				<Avatar
					name={displayName ?? companyName}
					colorSeed={senderEmail}
					image={resolvedImage}
					showAvatarFallback={!resolvedImage && !hasKnownSender}
					size={avatarSize}
					roundness="full"
				/>
			</Tooltip>
		)
	}

	const sourceLabel =
		source === "email"
			? "Email"
			: source === "slack"
				? "Slack"
				: source === "teams"
					? "Microsoft Teams"
					: source === "live-chat"
						? "Live chat"
						: source === "widget"
							? "Widget"
							: null

	const tooltipContent = (
		<div className="flex flex-col gap-0.5">
			<span>{format(timestamp, "MMMM d, yyyy 'at' HH:mm")}</span>
			{sourceLabel && <span className="text-gray-400">via {sourceLabel}</span>}
		</div>
	)

	const clockTime = isToday(timestamp)
		? format(timestamp, "HH:mm")
		: isThisYear(timestamp)
			? format(timestamp, "MMM d, HH:mm")
			: format(timestamp, "MMM d, yyyy, HH:mm")

	const timestampElement = (
		<div className="flex flex-none items-baseline gap-1.5 whitespace-nowrap leading-none">
			<Tooltip label={tooltipContent}>
				{timestampLink ? (
					<a
						className={cn(
							"text-xs",
							messageTimestampClasses(isOutbound),
							isOutbound ? "hover:opacity-100" : "hover:text-secondary",
						)}
						href={timestampLink}
						target="_blank"
						rel="noreferrer"
					>
						{clockTime}
					</a>
				) : (
					<span
						className={cn(
							"text-xs !leading-none",
							messageTimestampClasses(isOutbound),
						)}
					>
						{clockTime}
					</span>
				)}
			</Tooltip>
		</div>
	)

	if (isNotes) {
		return (
			<div
				className="relative -mx-2 min-h-[300px] rounded-2xl border border-gray-950/15 bg-white p-4 dark:border-white/10 dark:bg-gray-850"
				role="button"
				tabIndex={0}
				onClick={(e) => {
					if (e.currentTarget.contains(e.target as Node)) {
						onClick?.()
					}
				}}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{onHideNotes && (
					<div className="absolute right-3 top-3">
						<Button
							variant="ghost"
							onClick={(e) => {
								e.stopPropagation()
								onHideNotes()
							}}
							size="sm"
							className="text-secondary hover:text-primary"
						>
							Hide
						</Button>
					</div>
				)}
				<div className="mb-3 flex select-none items-center gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
					<span className="text-[0.875rem] font-semibold text-primary">
						Internal note
					</span>
					<span className="text-xs text-tertiary">·</span>
					{timestampElement}
				</div>
				<ZoomableContent className="cursor-text text-primary">
					{children}
				</ZoomableContent>
			</div>
		)
	}

	const nameRow = displayName ? (
		<div
			className={cn(
				"flex min-w-0 items-baseline gap-2 leading-5",
				shrinkActions && "flex-none",
			)}
		>
			<Tooltip label={avatarTooltipLabel} enabled={showIdentityTooltip}>
				<span className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-primary">
					{displayName}
				</span>
			</Tooltip>
			{isUnverified && (
				<Tooltip label="Unverified sender - enable SSO on your widget to authenticate users">
					<AlertTriangle
						color="orange"
						className="relative top-0.5 h-3.5 w-3.5 flex-none"
					/>
				</Tooltip>
			)}
			{timestampElement}
			{badge}
			{emailedAt ? (
				<Tooltip
					label={emailedReceiptLabel({
						sentAt: timestamp,
						emailedAt,
						recipient: emailedTo,
					})}
				>
					<div className="flex flex-none items-baseline gap-1 text-xs !leading-none text-tertiary">
						<EnvelopeArrowRight className="relative top-px h-3 w-3 flex-none" />
						<span>Emailed</span>
					</div>
				</Tooltip>
			) : readAt !== undefined ? (
				<div className="flex flex-none items-end gap-1 text-xs text-tertiary">
					{readAt ? (
						<Tooltip label={`Read at ${format(readAt, "HH:mm")}`}>
							<CheckCheck className="h-3 w-3" />
						</Tooltip>
					) : (
						<Tooltip label="Unread">
							<Check className="h-3 w-3" />
						</Tooltip>
					)}
				</div>
			) : null}
		</div>
	) : null

	// Slack layout: avatar column, then name row and body in one text column. The
	// column is pulled up so the name's cap height, not its line box, meets the avatar top.
	const messageColumns = (leading: ReactNode) => (
		<div className="flex w-full items-start gap-3">
			{leading}
			<div className="-mt-1 min-w-0 flex-1">
				{(nameRow || actions) && (
					<div className="flex items-start gap-3">
						{nameRow}
						{actions && (
							<div
								className={cn(
									"ml-auto flex h-5 items-center gap-1",
									shrinkActions ? "min-w-0" : "flex-shrink-0",
								)}
							>
								{actions}
							</div>
						)}
					</div>
				)}
				<ZoomableContent className="text-primary">{children}</ZoomableContent>
				{footer ? <div className={MESSAGE_FOOTER_CLASS}>{footer}</div> : null}
			</div>
		</div>
	)

	if (isComment) {
		const commentInner = messageColumns(
			showAvatar ? (
				<div className="flex-shrink-0">{renderAvatarContent()}</div>
			) : indentContent ? (
				<div className={cn("flex-none", MESSAGE_AVATAR_SLOT_CLASS)} />
			) : null,
		)

		if (bareComment) {
			return <div className={BARE_COMMENT_ROW_CLASS}>{commentInner}</div>
		}

		return (
			<div className={cn("py-1", { "pt-2": showAvatar })}>
				<div className="-mx-2 rounded-2xl border border-[#F2E6C9] bg-[#FFFBF2] p-3.5 dark:border-[#3D331C] dark:bg-[#221D12]">
					{commentInner}
				</div>
			</div>
		)
	}

	return (
		<div className="py-1">
			<div className={messageSurfaceClasses({ showBackground, isOutbound })}>
				{messageColumns(
					<div className="flex-shrink-0">{renderAvatarContent()}</div>,
				)}
			</div>
		</div>
	)
}
